import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import Navbar from "../../nav_bar/Navbar";
import Footer from "../../footer/Footer";
import { fetchVocabularyById } from "./vocabularyService";
import { BASE_ROOT } from "../../../../common/api_constant";
import "./VocabularyDetailPage.css";

const difficultyColor = (d) => {
 const map = {
 BEGINNER: "#4caf50",
 ELEMENTARY: "#8bc34a",
 INTERMEDIATE: "#ff9800",
 UPPER_INTERMEDIATE: "#ff5722",
 ADVANCED: "#e91e63",
 EXPERT: "#9c27b0",
 };
 return map[d?.toUpperCase()] || "#999";
};

const formatDifficulty = (d) =>
 d?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || d;

const VocabularyDetailPage = () => {
 const { id } = useParams();
 const navigate = useNavigate();
 const location = useLocation();
 const fromReading = location.state?.from === "reading";
 const readingId = location.state?.readingId;
 const fromGd = location.state?.from === "gd";
 const gdId = location.state?.gdId;
 const [word, setWord] = useState(null);
 const [loading, setLoading] = useState(true);

 const capitalizePartOfSpeech = (pos) => {
 if (!pos) return pos;
 return pos.charAt(0).toUpperCase() + pos.slice(1).toLowerCase();
 };

 useEffect(() => {
 const load = async () => {
 setLoading(true);
 const res = await fetchVocabularyById(id);
 if (res.status === 200 && res.data?.status) {
 setWord(res.data.data);
 }
 setLoading(false);
 };
 load();
 }, [id]);

 const backButton = (
  <button
    className="vd-back"
    onClick={() => {
      if (fromReading) return navigate(`/study/reading/${readingId}`);
      if (fromGd) return navigate(`/gd/${gdId}`);
      navigate("/study/vocabulary");
    }}
  >
    {fromReading ? "← Back" : fromGd ? "← Back to GD Topic" : "← Back to Vocabulary"}
  </button>
 );

 if (loading) {
  return (
   <>
    <Navbar />
    <div className="vd-page">
     <p className="vd-loading">Loading...</p>
    </div>
    <Footer />
   </>
  );
 }

 if (!word) {
  return (
   <>
    <Navbar />
    <div className="vd-page">
     <p className="vd-not-found">Word not found.</p>
     {backButton}
    </div>
    <Footer />
   </>
  );
 }

 return (
  <>
   <Navbar />
   <div className="vd-page">
    {backButton}

    {/* Hero */}
    <div className="vd-hero">
     <div className="vd-hero-top">
      <h1 className="vd-word">{word.word}</h1>
      <span
       className="vd-difficulty"
       style={{ background: difficultyColor(word.difficulty) }}
      >
       {formatDifficulty(word.difficulty)}
      </span>
     </div>

     {(word.pronunciation || word.phoneticSpelling) && (
      <div className="vd-pronunciations">
       {word.pronunciation && (
        <div className="vd-pron-item">
         <span className="vd-pron-label">
          IPA
          <Link to="/study/ipa/lessons" className="vd-ipa-info" title="Learn IPA symbols">
           ℹ
          </Link>
         </span>
         <span className="vd-pron-value">{word.pronunciation}</span>
        </div>
       )}
       {word.phoneticSpelling && (
        <div className="vd-pron-item">
         <span className="vd-pron-label">How to say</span>
         <span className="vd-pron-value phonetic">{word.phoneticSpelling}</span>
        </div>
       )}
      </div>
     )}

     {Array.isArray(word.partOfSpeech) && word.partOfSpeech.length > 0 ? (
      <div style={{ marginTop: '12px' }}>
       {word.partOfSpeech.map((pos, index) => (
        <span key={index} className="vd-pos" style={{ marginRight: '8px' }}>
         {capitalizePartOfSpeech(pos)}
        </span>
       ))}
      </div>
     ) : word.partOfSpeech ? (
      <span className="vd-pos">{capitalizePartOfSpeech(word.partOfSpeech)}</span>
     ) : null}
    </div>

    {word.imageUrl && (
     <div className="vd-image-wrapper">
      <img
       src={`${BASE_ROOT}${word.imageUrl}`}
       alt={word.word}
       className="vd-image"
      />
     </div>
    )}

    {/* Definition */}
    <div className="vd-card">
     <h3 className="vd-card-title">📝 Definition</h3>
     <p className="vd-card-text">{word.definition}</p>
    </div>

    {/* Examples */}
    {word.examples?.length > 0 && (
     <div className="vd-card">
      <h3 className="vd-card-title">💡 Examples</h3>
      <ul className="vd-examples">
       {word.examples.map((ex, i) => (
        <li key={i}>{ex}</li>
       ))}
      </ul>
     </div>
    )}

    {/* Etymology */}
    {word.etymology && (
     <div className="vd-card">
      <h3 className="vd-card-title">📜 Etymology</h3>
      <p className="vd-card-text">{word.etymology}</p>
     </div>
    )}

    {/* Synonyms & Antonyms */}
    {(word.synonyms?.length > 0 || word.antonyms?.length > 0) && (
     <div className="vd-row">
      {word.synonyms?.length > 0 && (
       <div className="vd-card vd-half">
        <h3 className="vd-card-title">✅ Synonyms</h3>
        <div className="vd-tags">
         {word.synonyms.map((s, i) => (
          <span key={i} className="vd-tag synonym">{s}</span>
         ))}
        </div>
       </div>
      )}

      {word.antonyms?.length > 0 && (
       <div className="vd-card vd-half">
        <h3 className="vd-card-title">❌ Antonyms</h3>
        <div className="vd-tags">
         {word.antonyms.map((a, i) => (
          <span key={i} className="vd-tag antonym">{a}</span>
         ))}
        </div>
       </div>
      )}
     </div>
    )}

    {/* YouTube Video */}
    {word.youtubeVideoLink && (
     <div className="vd-card vd-video-card">
      <h3 className="vd-card-title">🎬 Video</h3>
      <div className="vd-video-wrapper">
       <iframe
        src={word.youtubeVideoLink
         .replace("watch?v=", "embed/")
         .replace("youtu.be/", "youtube.com/embed/")}
        title={word.word}
        allowFullScreen
        frameBorder="0"
       />
      </div>
     </div>
    )}

    {/* Meta */}
    {word.createdAt && (
     <p className="vd-meta">
      Added on{" "}
      {new Date(word.createdAt).toLocaleDateString("en-IN", {
       day: "numeric",
       month: "short",
       year: "numeric",
      })}
     </p>
    )}
   </div>
   <Footer />
  </>
 );
};

export default VocabularyDetailPage;
